import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, MessageCircle, Clock, Wrench, BookOpen, HelpCircle, Shield } from "lucide-react";

export const Route = createFileRoute("/contato")({
  head: () => ({
    meta: [
      { title: "Contato MEI Toolbox | Suporte e Ferramentas para MEI" },
      {
        name: "description",
        content:
          "Fale com a equipe do MEI Toolbox: tire dúvidas sobre DAS, nota fiscal, formalização do MEI e nossas ferramentas grátis. Respondemos em até 48 horas úteis.",
      },
      { property: "og:title", content: "Contato MEI Toolbox | Suporte e Ferramentas para MEI" },
      {
        property: "og:description",
        content:
          "Suporte, dúvidas, parcerias e sugestões — a equipe do MEI Toolbox responde em até 48h úteis.",
      },
      { property: "og:url", content: "https://meitoolbox.com.br/contato" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Contato MEI Toolbox | Suporte para o microempreendedor" },
      {
        name: "twitter:description",
        content:
          "Tire dúvidas sobre MEI, DAS, nota fiscal e use as ferramentas gratuitas do MEI Toolbox.",
      },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/contato" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contato MEI Toolbox",
          url: "https://meitoolbox.com.br/contato",
          inLanguage: "pt-BR",
          isPartOf: { "@id": "https://meitoolbox.com.br/#website" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Início",
              item: "https://meitoolbox.com.br/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Contato",
              item: "https://meitoolbox.com.br/contato",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "O MEI Toolbox cobra alguma coisa para usar as ferramentas?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Não. Todas as ferramentas para MEI — calculadora de DAS, gerador de recibo, validador de CNPJ, precificação e mais — são 100% gratuitas e não exigem cadastro.",
              },
            },
            {
              "@type": "Question",
              name: "A equipe do MEI Toolbox presta consultoria contábil para microempreendedores?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Não somos um escritório de contabilidade. Oferecemos ferramentas e conteúdo educativo sobre MEI, DAS, nota fiscal e gestão financeira. Para situações específicas, recomendamos um contador parceiro.",
              },
            },
            {
              "@type": "Question",
              name: "Em quanto tempo recebo resposta no contato?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Respondemos e-mails em até 48 horas úteis. Dúvidas frequentes sobre DAS, nota fiscal e formalização também são respondidas nos nossos artigos.",
              },
            },
            {
              "@type": "Question",
              name: "Posso sugerir uma nova ferramenta para MEI?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Sim! Sugestões de ferramentas para gestão financeira do MEI, cálculos, geradores de documentos e validadores são muito bem-vindas. Use o formulário ou nos envie um e-mail.",
              },
            },
            {
              "@type": "Question",
              name: "Os meus dados ficam salvos no MEI Toolbox?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Nada do que você digita nas ferramentas (CNPJ, faturamento, dados do recibo) é enviado para nossos servidores. Tudo é processado e fica salvo apenas no seu navegador.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: ContatoPage,
});

function ContatoPage() {
  return (
    <main className="container mx-auto px-4 py-16 max-w-3xl">
      <header className="text-center mb-12">
        <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
          Suporte e parcerias
        </p>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
          Fale com o MEI Toolbox
        </h1>
        <p className="text-lg text-muted-foreground">
          Tem dúvidas sobre as <strong>ferramentas gratuitas para MEI</strong>, DAS, nota fiscal ou
          formalização? Quer sugerir uma nova calculadora ou propor uma parceria? Estamos por aqui
          para ajudar o microempreendedor individual brasileiro.
        </p>
      </header>

      <div className="grid gap-6 sm:grid-cols-2 mb-12">
        <a
          href="mailto:contato@meitoolbox.com.br"
          className="flex items-start gap-4 rounded-2xl border border-border bg-card p-6 hover:border-primary/50 transition"
        >
          <Mail className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <h2 className="font-semibold mb-1">E-mail</h2>
            <p className="text-sm text-muted-foreground break-all">contato@meitoolbox.com.br</p>
          </div>
        </a>
        <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-6">
          <Clock className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <h2 className="font-semibold mb-1">Tempo de resposta</h2>
            <p className="text-sm text-muted-foreground">Até 48h úteis para todos os contatos.</p>
          </div>
        </div>
      </div>

      <form className="space-y-4 rounded-2xl border border-border bg-card p-6 sm:p-8 mb-16">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="nome" className="block text-sm font-medium mb-1">
              Nome
            </label>
            <input
              id="nome"
              type="text"
              required
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              E-mail
            </label>
            <input
              id="email"
              type="email"
              required
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
        <div>
          <label htmlFor="assunto" className="block text-sm font-medium mb-1">
            Assunto
          </label>
          <input
            id="assunto"
            type="text"
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label htmlFor="mensagem" className="block text-sm font-medium mb-1">
            Mensagem
          </label>
          <textarea
            id="mensagem"
            rows={5}
            required
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-soft hover:opacity-90 transition"
        >
          Enviar mensagem
        </button>
      </form>

      <section className="prose prose-lg max-w-none dark:prose-invert mb-16">
        <h2 className="font-display text-3xl font-bold">Sobre o MEI Toolbox</h2>
        <p>
          O <strong>MEI Toolbox</strong> é a caixa de ferramentas digital do microempreendedor
          individual brasileiro. Reunimos em um só lugar{" "}
          <strong>calculadoras, geradores de documentos e validadores gratuitos</strong> para quem
          precisa cuidar do próprio CNPJ no dia a dia — sem juridiquês, sem cadastro e sem custo.
          Nosso objetivo é tornar a <strong>gestão financeira do MEI</strong> simples, mesmo para
          quem está abrindo o primeiro negócio agora.
        </p>
        <p>
          Atendemos diariamente{" "}
          <strong>
            microempreendedores individuais, prestadores de serviço, vendedores online,
            profissionais autônomos e pequenos comerciantes
          </strong>{" "}
          que usam o site para calcular DAS, simular limite de faturamento, emitir recibos, validar
          CNPJ, criar contratos em PDF e tirar dúvidas sobre nota fiscal, INSS, Simples Nacional e
          obrigações mensais do MEI.
        </p>

        <h2 className="font-display text-3xl font-bold">Como podemos ajudar você</h2>
        <p>Use o canal certo para receber resposta mais rápida:</p>
        <ul>
          <li>
            <strong>Dúvidas sobre as ferramentas</strong> (calculadora DAS MEI, validador de CNPJ,
            gerador de recibo, precificação): escreva para{" "}
            <a href="mailto:contato@meitoolbox.com.br">contato@meitoolbox.com.br</a> descrevendo o
            passo onde travou.
          </li>
          <li>
            <strong>Sugestões de novas ferramentas para MEI</strong>: conta o problema que você quer
            resolver. As melhores ideias entram no nosso roadmap.
          </li>
          <li>
            <strong>Erro técnico no site</strong>: descreva o navegador, o aparelho e, se possível,
            envie um print da tela.
          </li>
          <li>
            <strong>Parcerias e imprensa</strong>: contadores, fintechs, escritórios de
            contabilidade e veículos de mídia que falam sobre microempreendedor individual podem
            entrar em contato pelo mesmo e-mail.
          </li>
          <li>
            <strong>Conteúdo sobre MEI</strong>: para correções em artigos, sugestões de temas ou
            citações, envie a referência da página.
          </li>
        </ul>

        <h2 className="font-display text-3xl font-bold">
          O que <em>não</em> fazemos
        </h2>
        <p>
          Para ser transparente: o MEI Toolbox <strong>não é um escritório de contabilidade</strong>{" "}
          e não presta consultoria tributária personalizada. Não emitimos DAS no seu lugar, não
          fazemos sua DASN-SIMEI nem regularizamos pendências junto à Receita Federal. O que fazemos
          é dar a você as <strong>ferramentas gratuitas para MEI</strong> e o conteúdo educativo
          necessário para entender cada etapa — e, quando o caso pede acompanhamento profissional,
          indicamos buscar um contador parceiro.
        </p>

        <h2 className="font-display text-3xl font-bold">
          Antes de escrever: confira se a resposta já está aqui
        </h2>
        <p>
          A maioria das dúvidas do microempreendedor individual já está respondida nas nossas
          ferramentas e artigos. Vale dar uma olhada antes:
        </p>
        <ul>
          <li>
            Para calcular o DAS do mês ou simular atrasos, use a{" "}
            <Link to="/ferramentas/$slug" params={{ slug: "calculadora-das-mei" }}>
              Calculadora DAS MEI
            </Link>
            .
          </li>
          <li>
            Para checar se está perto do teto, abra o{" "}
            <Link to="/ferramentas/$slug" params={{ slug: "limite-faturamento-mei" }}>
              Simulador de limite de faturamento
            </Link>
            .
          </li>
          <li>
            Para confirmar dados de empresa, use o{" "}
            <Link to="/ferramentas/$slug" params={{ slug: "validador-cnpj" }}>
              Validador de CNPJ
            </Link>
            .
          </li>
          <li>
            Para entender obrigações mensais do MEI, leia o nosso{" "}
            <Link to="/artigos/$slug" params={{ slug: "obrigacoes-mensais-mei" }}>
              checklist 2026
            </Link>
            .
          </li>
        </ul>

        <h2 className="font-display text-3xl font-bold">Privacidade e segurança</h2>
        <p>
          Quando você usa nossas <strong>ferramentas para MEI</strong>, nada do que digita (CNPJ,
          faturamento, valor do recibo, dados do contrato) é enviado para nossos servidores. Todo
          cálculo roda no seu próprio navegador e o histórico fica salvo apenas no seu aparelho.
          Para ler nossas regras completas, veja a{" "}
          <Link to="/privacidade">Política de Privacidade</Link>, os{" "}
          <Link to="/termos">Termos de Uso</Link> e a <Link to="/cookies">Política de Cookies</Link>
          .
        </p>

        <h2 className="font-display text-3xl font-bold">
          Perguntas frequentes sobre contato e suporte
        </h2>

        <h3 className="font-semibold text-xl">
          O MEI Toolbox cobra alguma coisa para usar as ferramentas?
        </h3>
        <p>
          Não. Todas as ferramentas para MEI — calculadora de DAS, gerador de recibo, validador de
          CNPJ, precificação e mais — são 100% gratuitas e não exigem cadastro.
        </p>

        <h3 className="font-semibold text-xl">
          A equipe do MEI Toolbox presta consultoria contábil para microempreendedores?
        </h3>
        <p>
          Não somos um escritório de contabilidade. Oferecemos ferramentas e conteúdo educativo
          sobre MEI, DAS, nota fiscal e gestão financeira. Para situações específicas, recomendamos
          um contador parceiro.
        </p>

        <h3 className="font-semibold text-xl">Em quanto tempo recebo resposta no contato?</h3>
        <p>
          Respondemos e-mails em até 48 horas úteis. Dúvidas frequentes sobre DAS, nota fiscal e
          formalização também são respondidas nos nossos artigos.
        </p>

        <h3 className="font-semibold text-xl">Posso sugerir uma nova ferramenta para MEI?</h3>
        <p>
          Sim! Sugestões de ferramentas para gestão financeira do MEI, cálculos, geradores de
          documentos e validadores são muito bem-vindas. Use o formulário ou nos envie um e-mail.
        </p>

        <h3 className="font-semibold text-xl">Os meus dados ficam salvos no MEI Toolbox?</h3>
        <p>
          Nada do que você digita nas ferramentas (CNPJ, faturamento, dados do recibo) é enviado
          para nossos servidores. Tudo é processado e fica salvo apenas no seu navegador.
        </p>
      </section>

      <section className="rounded-2xl border border-border bg-gradient-hero text-white p-8 md:p-10 text-center">
        <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
          Comece agora sem esperar resposta
        </h2>
        <p className="text-white/85 mb-6 max-w-xl mx-auto">
          Mais de 30 ferramentas grátis para MEI estão disponíveis para uso imediato — calcule DAS,
          gere recibo, valide CNPJ e muito mais.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/ferramentas"
            className="inline-flex items-center gap-2 rounded-full bg-white text-primary px-6 py-2.5 text-sm font-bold hover:opacity-90 transition"
          >
            <Wrench className="h-4 w-4" /> Ver todas as ferramentas
          </Link>
          <Link
            to="/artigos"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-2.5 text-sm font-bold hover:bg-white/10 transition"
          >
            <BookOpen className="h-4 w-4" /> Ler artigos
          </Link>
        </div>
        <p className="mt-6 text-xs text-white/70 flex items-center justify-center gap-4 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <MessageCircle className="h-3.5 w-3.5" /> Resposta em 48h
          </span>
          <span className="inline-flex items-center gap-1">
            <HelpCircle className="h-3.5 w-3.5" /> Sem cadastro
          </span>
          <span className="inline-flex items-center gap-1">
            <Shield className="h-3.5 w-3.5" /> Dados no seu navegador
          </span>
        </p>
      </section>
    </main>
  );
}
